import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { searchUserByEmail, getUserBySessionMId, depositPoints } from '../services/api'

const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1.4rem;
  min-width: 200px;
  background: white;
`

const FileInput = styled.input`
  font-size: 1.3rem;
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: ${theme.color.accent.primary};
    color: white;
    &:hover:not(:disabled) { background: #b5002d; }
    &:disabled { background: #ccc; cursor: not-allowed; }
  ` : props.variant === 'danger' ? `
    background: #dc3545;
    color: white;
    &:hover { background: #c82333; }
  ` : `
    background: white;
    color: ${theme.color.text.base};
    border: 1px solid ${theme.color.border.light};
    &:hover { background: ${theme.color.background.lightGrey}; }
  `}
`

const TemplateLink = styled.a`
  font-size: 1.3rem;
  color: ${theme.color.accent.primary};
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: #b5002d; }
`

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: 1.3rem;
`

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  background: ${theme.color.background.lightGrey};
  border-bottom: 2px solid ${theme.color.border.light};
  font-weight: 600;
`

const Td = styled.td<{ status?: string }>`
  padding: 0.75rem;
  border-bottom: 1px solid ${theme.color.border.light};
  color: ${props =>
    props.status === 'success' ? '#28a745' :
    props.status === 'error' ? '#dc3545' :
    props.status === 'processing' ? '#007bff' :
    theme.color.text.base};
`

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.color.border.light};
  border-radius: 4px;
  margin: 1rem 0;
  overflow: hidden;
`

const ProgressFill = styled.div<{ percent: number }>`
  width: ${props => props.percent}%;
  height: 100%;
  background: ${theme.color.accent.primary};
  border-radius: 4px;
  transition: width 0.3s ease;
`

const StatusSummary = styled.div`
  display: flex;
  gap: 2rem;
  margin: 1rem 0;
  font-size: 1.4rem;
  font-weight: 600;
`

const StatusItem = styled.span<{ color: string }>`
  color: ${props => props.color};
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 1.3rem;
`

const InfoMessage = styled.div`
  background: #e6f3ff;
  color: #0056b3;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`

// Deposit type → point source name mapping
const DEPOSIT_CONFIG: Record<string, string> = {
  'customer_service': 'Customer Service',
  'challenge': 'Completed a Challenge',
  'purchases': 'Retail',
}

interface CsvRow {
  identifier: string
  identifierType: 'email' | 'sessionm_id' | 'mariana_tek_id'
  points: number
  sessionmId?: string
  status: 'pending' | 'processing' | 'success' | 'error'
  message?: string
}

interface BulkPointsProps {
  userData?: any
}

const BulkPoints: React.FC<BulkPointsProps> = () => {
  const [depositType, setDepositType] = useState('')
  const [rows, setRows] = useState<CsvRow[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [processed, setProcessed] = useState(0)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const downloadTemplate = () => {
    const csv = 'email,points\n'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'bulk_points_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError('')
    setProcessed(0)

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string
        const lines = text.trim().split('\n')
        if (lines.length < 2) {
          setError('CSV must have a header row and at least one data row')
          return
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''))

        // Detect identifier column
        let identifierCol = -1
        let identifierType: 'email' | 'sessionm_id' | 'mariana_tek_id' = 'email'

        for (let i = 0; i < headers.length; i++) {
          const h = headers[i]
          if (h === 'email' || h === 'e-mail' || h === 'email_address') {
            identifierCol = i
            identifierType = 'email'
            break
          } else if (h === 'sessionm_id' || h === 'sessionm id' || h === 'sm_id') {
            identifierCol = i
            identifierType = 'sessionm_id'
            break
          } else if (h === 'mariana_tek_id' || h === 'mt_id' || h === 'mariana tek id') {
            identifierCol = i
            identifierType = 'mariana_tek_id'
            break
          }
        }

        if (identifierCol === -1) {
          setError('CSV must have a header column named: email, sessionm_id, or mariana_tek_id')
          return
        }

        // Find points column
        const pointsCol = headers.findIndex(h => h === 'points' || h === 'amount' || h === 'point_amount')
        if (pointsCol === -1) {
          setError('CSV must have a header column named: points or amount')
          return
        }

        const parsed: CsvRow[] = []
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map(c => c.trim().replace(/['"]/g, ''))
          const identifier = cols[identifierCol]
          const points = parseInt(cols[pointsCol], 10)

          if (!identifier || isNaN(points) || points <= 0) continue

          parsed.push({
            identifier,
            identifierType,
            points,
            status: 'pending',
          })
        }

        if (parsed.length === 0) {
          setError('No valid rows found in CSV')
          return
        }

        setRows(parsed)
      } catch (err) {
        setError('Failed to parse CSV file')
      }
    }
    reader.readAsText(file)
  }

  const resolveSessionMId = async (row: CsvRow): Promise<string | null> => {
    try {
      if (row.identifierType === 'sessionm_id') {
        return row.identifier
      }

      if (row.identifierType === 'email') {
        const result = await searchUserByEmail(row.identifier)
        // Response can be { user: {...} } or { users: [...] }
        if (result?.user?.id) {
          return result.user.id
        }
        const users = result?.users || result?.result?.users || []
        if (users.length > 0) {
          return users[0].id || users[0].user_id
        }
        return null
      }

      if (row.identifierType === 'mariana_tek_id') {
        // Look up user by MT external_id
        const result = await getUserBySessionMId(row.identifier)
        if (result?.user?.id) {
          return result.user.id
        }
        return null
      }

      return null
    } catch {
      return null
    }
  }

  const processDeposits = async () => {
    if (!depositType) {
      setError('Please select a deposit type')
      return
    }
    if (rows.length === 0) {
      setError('Please upload a CSV file first')
      return
    }

    setIsProcessing(true)
    setProcessed(0)
    setError('')

    const pointSource = DEPOSIT_CONFIG[depositType]
    const updatedRows = [...rows]

    for (let i = 0; i < updatedRows.length; i++) {
      updatedRows[i] = { ...updatedRows[i], status: 'processing', message: 'Resolving user...' }
      setRows([...updatedRows])

      try {
        // Resolve SessionM ID
        const sessionmId = await resolveSessionMId(updatedRows[i])
        if (!sessionmId) {
          updatedRows[i] = { ...updatedRows[i], status: 'error', message: 'User not found' }
          setRows([...updatedRows])
          setProcessed(i + 1)
          continue
        }

        updatedRows[i] = { ...updatedRows[i], sessionmId, message: 'Depositing points...' }
        setRows([...updatedRows])

        // depositPoints already handles dual deposit (source account + tier tracked account)
        await depositPoints(sessionmId, pointSource, updatedRows[i].points)

        updatedRows[i] = { ...updatedRows[i], status: 'success', message: `${updatedRows[i].points} pts deposited` }
      } catch (err) {
        updatedRows[i] = {
          ...updatedRows[i],
          status: 'error',
          message: err instanceof Error ? err.message : 'Deposit failed',
        }
      }

      setRows([...updatedRows])
      setProcessed(i + 1)
    }

    setIsProcessing(false)
  }

  const reset = () => {
    setRows([])
    setProcessed(0)
    setError('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const successCount = rows.filter(r => r.status === 'success').length
  const errorCount = rows.filter(r => r.status === 'error').length
  const pendingCount = rows.filter(r => r.status === 'pending' || r.status === 'processing').length
  const progressPercent = rows.length > 0 ? (processed / rows.length) * 100 : 0

  return (
    <Container>
      <Title>Bulk Points Deposit</Title>

      <InfoMessage>
        Upload a CSV file with user identifiers and point amounts. The file must have a header row with
        a user column (<strong>email</strong>, <strong>sessionm_id</strong>, or <strong>mariana_tek_id</strong>)
        and a <strong>points</strong> column. Points will be deposited to the appropriate source and also
        credited to the Tier tracked account.
      </InfoMessage>

      <ControlRow>
        <div>
          <label style={{ fontSize: '1.3rem', fontWeight: 600, marginRight: '0.5rem' }}>Deposit Type:</label>
          <Select value={depositType} onChange={e => setDepositType(e.target.value)} disabled={isProcessing}>
            <option value="">-- Select Type --</option>
            <option value="customer_service">Customer Service</option>
            <option value="challenge">Challenge</option>
            <option value="purchases">Purchases</option>
          </Select>
        </div>

        <div>
          <label style={{ fontSize: '1.3rem', fontWeight: 600, marginRight: '0.5rem' }}>CSV File:</label>
          <FileInput
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={isProcessing}
          />
        </div>

        <TemplateLink onClick={downloadTemplate}>Download Template</TemplateLink>
      </ControlRow>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {rows.length > 0 && (
        <>
          <ControlRow>
            <Button
              variant="primary"
              onClick={processDeposits}
              disabled={isProcessing || !depositType}
            >
              {isProcessing ? `Processing ${processed}/${rows.length}...` : `Deposit Points for ${rows.length} Users`}
            </Button>
            <Button variant="secondary" onClick={reset} disabled={isProcessing}>
              Clear
            </Button>
          </ControlRow>

          {isProcessing && (
            <ProgressBar>
              <ProgressFill percent={progressPercent} />
            </ProgressBar>
          )}

          {processed > 0 && (
            <StatusSummary>
              <StatusItem color="#28a745">Success: {successCount}</StatusItem>
              <StatusItem color="#dc3545">Failed: {errorCount}</StatusItem>
              {pendingCount > 0 && <StatusItem color="#007bff">Remaining: {pendingCount}</StatusItem>}
            </StatusSummary>
          )}

          <PreviewTable>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>{rows[0]?.identifierType === 'email' ? 'Email' :
                     rows[0]?.identifierType === 'sessionm_id' ? 'SessionM ID' : 'MT ID'}</Th>
                <Th>Points</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <Td>{i + 1}</Td>
                  <Td>{row.identifier}</Td>
                  <Td>{row.points}</Td>
                  <Td status={row.status}>
                    {row.status === 'pending' ? 'Pending' : row.message || row.status}
                  </Td>
                </tr>
              ))}
            </tbody>
          </PreviewTable>
        </>
      )}
    </Container>
  )
}

export default BulkPoints
